/**
 * Daireler presenter'i
 */

import { AppState } from '../../modules/state.js';
import { FirebaseService } from '../../shared/services/firebase.service.js';
import { COLLECTIONS, MONTHS } from '../../firebase-config.js';
import { formatDate } from '../../shared/utils/formatters.js';
import { normalizeApartmentRecord, serializeApartmentRecord } from '../../shared/utils/apartment-schema.js';
import { manageResidentAccess } from './apartment-access.service.js';

interface ApartmentsPresenterDeps {
    openModal: (modalId: string) => void;
    closeModal: (modalId: string) => void;
    showToast: (message: string, type?: string) => void;
    escapeHtml: (value: string) => string;
    totalApartments: number;
}

function findApartmentById(id: string | number) {
    return (AppState.apartments || []).find(apartment => apartment.id === id || apartment.number == id);
}

export function renderApartmentsPresenter(deps: ApartmentsPresenterDeps): void {
    const container = document.getElementById('apartments-list');
    const searchInput = document.getElementById('apartment-search') as HTMLInputElement | null;
    const statusFilter = document.getElementById('apartment-status-filter') as HTMLSelectElement | null;
    if (!container) return;

    const searchTerm = searchInput?.value?.toLowerCase() || '';
    const statusValue = statusFilter?.value || 'all';

    const allApartments = (AppState.apartments || []).map(apartment => normalizeApartmentRecord(apartment)).sort((a, b) => a.number - b.number);
    const apartmentMap: Record<number, any> = {};
    allApartments.forEach(apartment => {
        apartmentMap[apartment.number] = apartment;
    });

    const apartments = [];
    for (let number = 1; number <= deps.totalApartments; number++) {
        apartments.push(apartmentMap[number] || normalizeApartmentRecord({ number, residentName: '', status: 'empty' }));
    }

    let occupiedCount = 0;
    let ownerCount = 0;
    let tenantCount = 0;

    apartments.forEach(apartment => {
        const hasResident = apartment.residentName && apartment.residentName.trim() !== '' && apartment.residentName.trim() !== '-';
        if (hasResident) occupiedCount++;
        if (apartment.status === 'owner') ownerCount++;
        if (apartment.status === 'tenant') tenantCount++;
    });

    const filtered = apartments.filter(apartment => {
        if (searchTerm) {
            const name = (apartment.residentName || '').toLowerCase();
            const owner = (apartment.ownerName || '').toLowerCase();
            const phone = (apartment.phone || '').toLowerCase();
            if (!name.includes(searchTerm) && !owner.includes(searchTerm) && !phone.includes(searchTerm)) {
                return false;
            }
        }

        if (statusValue !== 'all' && apartment.status !== statusValue) {
            return false;
        }

        return true;
    });

    const statusClass: Record<string, string> = { owner: 'success', tenant: 'warning', empty: 'secondary' };
    const statusLabel: Record<string, string> = { owner: 'Ev Sahibi', tenant: 'Kiracı', empty: 'Boş' };
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = AppState.currentYear;

    if (filtered.length === 0) {
        container.innerHTML = '<p class="empty-state">Arama kriterlerine uygun daire bulunamadı</p>';
    } else {
        container.innerHTML = filtered.map(apartment => {
            const isDuePaid = AppState.dues[currentYear]?.[apartment.number]?.[currentMonth] || false;
            const dueStatusHtml = isDuePaid
                ? '<span class="due-badge paid">✓ Aidat Ödendi</span>'
                : '<span class="due-badge unpaid">✗ Aidat Ödenmedi</span>';
            const qualityWarnings: string[] = [];

            if (apartment.status !== 'empty') {
                if (!apartment.phone?.trim()) qualityWarnings.push('Telefon eksik');
                if (!apartment.email?.trim()) qualityWarnings.push('E-posta eksik');
            }

            return `
            <div class="apartment-card glass-card" data-apartment="${apartment.number}">
                <div class="apt-header">
                    <div class="apt-number">Daire ${apartment.number}</div>
                    <div class="apt-status badge ${statusClass[apartment.status] || 'secondary'}">${statusLabel[apartment.status] || apartment.status}</div>
                </div>
                <div class="apt-details">
                    <div class="apt-detail-row">
                        <span class="apt-label">👤 Sakin:</span>
                        <span class="apt-value">${deps.escapeHtml(apartment.residentName || '-')}</span>
                    </div>
                    <div class="apt-detail-row">
                        <span class="apt-label">📞 Telefon:</span>
                        <span class="apt-value">${deps.escapeHtml(apartment.phone || '-')}</span>
                    </div>
                    <div class="apt-detail-row">
                        <span class="apt-label">✉️ E-posta:</span>
                        <span class="apt-value apt-email">${deps.escapeHtml(apartment.email || '-')}</span>
                    </div>
                    <div class="apt-detail-row">
                        <span class="apt-label">👥 Kişi:</span>
                        <span class="apt-value">${apartment.residentCount || 0}</span>
                    </div>
                </div>
                ${qualityWarnings.length > 0 ? `
                <div class="apt-quality-warnings">
                    ${qualityWarnings.map(warning => `<span class="apt-warning-badge">${warning}</span>`).join('')}
                </div>
                ` : ''}
                <div class="apt-due-status">
                    ${dueStatusHtml}
                </div>
                <div class="apt-actions">
                    <button class="btn btn-outline btn-sm" onclick="viewApartmentDetail('${apartment.id || apartment.number}')">👁️ Detay</button>
                    <button class="btn btn-secondary btn-sm" onclick="editApartment('${apartment.id || apartment.number}')">✏️ Düzenle</button>
                </div>
            </div>
            `;
        }).join('');
    }

    document.getElementById('occupied-apartments')!.textContent = String(occupiedCount);
    document.getElementById('owner-apartments')!.textContent = String(ownerCount);
    document.getElementById('tenant-apartments')!.textContent = String(tenantCount);
    document.getElementById('apartments-total')!.textContent = String(deps.totalApartments);
}

export function editApartmentPresenter(id: string | number, deps: ApartmentsPresenterDeps): void {
    let apartment = findApartmentById(id);

    if (!apartment && !Number.isNaN(Number(id))) {
        apartment = { number: Number(id), residentName: '', phone: '', status: 'empty', ownerName: '', residentCount: 0 };
    }

    if (!apartment) return;

    const normalized = normalizeApartmentRecord(apartment);
    document.getElementById('apartment-modal-title')!.textContent = `Daire ${normalized.number} Düzenle`;
    (document.getElementById('apartment-id') as HTMLInputElement).value = String(normalized.id || normalized.number);
    (document.getElementById('apartment-resident') as HTMLInputElement).value = normalized.residentName || '';
    (document.getElementById('apartment-status') as HTMLSelectElement).value = normalized.status || 'empty';
    (document.getElementById('apartment-owner') as HTMLInputElement).value = normalized.ownerName || '';
    (document.getElementById('apartment-phone') as HTMLInputElement).value = normalized.phone || '';
    (document.getElementById('apartment-count') as HTMLInputElement).value = String(normalized.residentCount || 0);
    (document.getElementById('apartment-move-in') as HTMLInputElement).value = normalized.moveInDate || '';
    (document.getElementById('apartment-email') as HTMLInputElement).value = normalized.email || '';
    const passwordInput = document.getElementById('apartment-password') as HTMLInputElement | null;
    const passwordConfirmInput = document.getElementById('apartment-password-confirm') as HTMLInputElement | null;
    if (passwordInput) passwordInput.value = '';
    if (passwordConfirmInput) passwordConfirmInput.value = '';
    document.getElementById('apartment-form')!.dataset.aptNumber = String(normalized.number);

    deps.openModal('apartment-modal');
}

export async function saveApartmentPresenter(data: Record<string, any>, deps: ApartmentsPresenterDeps): Promise<void> {
    const number = parseInt(document.getElementById('apartment-form')!.dataset.aptNumber || '0', 10);
    const existing = (AppState.apartments || []).find(apartment => apartment.number === number);
    const id = existing?.id || null;
    const residentPassword = typeof data.residentPassword === 'string' ? data.residentPassword.trim() : '';
    const shouldDisableResidentAccess = data.status === 'empty';
    const shouldUpdateResidentPassword = !shouldDisableResidentAccess && residentPassword.length >= 6;
    const {
        residentPassword: _residentPassword,
        residentPasswordConfirm: _residentPasswordConfirm,
        ...apartmentFormData
    } = data;

    let apartmentData = { ...apartmentFormData, number };
    if (data.status === 'empty') {
        apartmentData = {
            number,
            status: 'empty',
            residentName: '',
            ownerName: '',
            phone: '',
            email: '',
            residentCount: 0,
            moveInDate: ''
        };
    }

    const serialized = serializeApartmentRecord(apartmentData);

    if (id) {
        const index = AppState.apartments.findIndex(apartment => apartment.id === id);
        AppState.apartments[index] = { ...AppState.apartments[index], ...serialized };
        await FirebaseService.save(COLLECTIONS.APARTMENTS, id, serialized);
    } else {
        const newId = await FirebaseService.add(COLLECTIONS.APARTMENTS, serialized);
        serialized.id = newId;
        AppState.apartments.push(serialized);
    }

    try {
        if (shouldDisableResidentAccess) {
            await manageResidentAccess({
                apartmentNumber: number,
                disable: true
            });
        } else if (shouldUpdateResidentPassword) {
            await manageResidentAccess({
                apartmentNumber: number,
                password: residentPassword,
                disable: false
            });
        }
    } catch (error) {
        renderApartmentsPresenter(deps);
        throw new Error('Daire bilgileri kaydedildi ancak sakin şifresi güncellenemedi.');
    }

    renderApartmentsPresenter(deps);

    if (shouldDisableResidentAccess) {
        deps.showToast('Daire bilgileri güncellendi, sakin girişi pasifleştirildi', 'success');
    } else if (shouldUpdateResidentPassword) {
        deps.showToast('Daire bilgileri ve sakin şifresi güncellendi', 'success');
    } else {
        deps.showToast('Daire bilgileri güncellendi', 'success');
    }
}

export function viewApartmentDetailPresenter(id: string | number, deps: ApartmentsPresenterDeps): void {
    let apartment = findApartmentById(id);

    if (!apartment && !Number.isNaN(Number(id))) {
        apartment = { number: Number(id), residentName: '', phone: '', status: 'empty', ownerName: '', residentCount: 0 };
    }

    if (!apartment) return;

    const normalized = normalizeApartmentRecord(apartment);
    const statusLabel: Record<string, string> = { owner: 'Ev Sahibi', tenant: 'Kiracı', empty: 'Boş' };
    const currentYear = AppState.currentYear;
    const currentMonth = new Date().getMonth() + 1;
    let paidMonths = 0;
    let unpaidMonths = 0;
    const duesHtml: string[] = [];

    for (let month = 1; month <= 12; month++) {
        const isPaid = AppState.dues[currentYear]?.[normalized.number]?.[month] || false;
        if (isPaid) paidMonths++;
        else if (month <= currentMonth) unpaidMonths++;

        const statusClass = isPaid ? 'paid' : (month <= currentMonth ? 'unpaid' : 'future');
        const statusIcon = isPaid ? '✓' : (month <= currentMonth ? '✗' : '○');
        duesHtml.push(`<span class="due-month-badge ${statusClass}" title="${MONTHS[month - 1]}">${statusIcon}</span>`);
    }

    document.getElementById('apartment-detail-title')!.textContent = `🏠 Daire ${normalized.number} Detayı`;
    document.getElementById('apartment-detail-content')!.innerHTML = `
        <div class="apartment-detail-grid">
            <div class="detail-section">
                <h4>👤 Sakin Bilgileri</h4>
                <div class="detail-item">
                    <span class="detail-label">Ad Soyad:</span>
                    <span class="detail-value">${deps.escapeHtml(normalized.residentName || '-')}</span>
                </div>
                <div class="detail-item">
                    <span class="detail-label">Durum:</span>
                    <span class="detail-value badge ${normalized.status === 'owner' ? 'success' : normalized.status === 'tenant' ? 'warning' : 'secondary'}">${statusLabel[normalized.status] || '-'}</span>
                </div>
                <div class="detail-item">
                    <span class="detail-label">Kişi Sayısı:</span>
                    <span class="detail-value">${normalized.residentCount || 0}</span>
                </div>
                <div class="detail-item">
                    <span class="detail-label">Giriş Tarihi:</span>
                    <span class="detail-value">${normalized.moveInDate ? formatDate(normalized.moveInDate) : '-'}</span>
                </div>
            </div>
            <div class="detail-section">
                <h4>📞 İletişim Bilgileri</h4>
                <div class="detail-item">
                    <span class="detail-label">Telefon:</span>
                    <span class="detail-value">${deps.escapeHtml(normalized.phone || '-')}</span>
                </div>
                <div class="detail-item">
                    <span class="detail-label">E-posta:</span>
                    <span class="detail-value">${deps.escapeHtml(normalized.email || '-')}</span>
                </div>
            </div>
            <div class="detail-section">
                <h4>🏡 Mülk Sahibi</h4>
                <div class="detail-item">
                    <span class="detail-label">Ad Soyad:</span>
                    <span class="detail-value">${deps.escapeHtml(normalized.ownerName || '-')}</span>
                </div>
            </div>
            <div class="detail-section full-width">
                <h4>💰 ${currentYear} Aidat Durumu</h4>
                <div class="due-months-grid">
                    ${duesHtml.join('')}
                </div>
                <div class="due-summary">
                    <span class="due-stat paid">✓ ${paidMonths} Ödendi</span>
                    <span class="due-stat unpaid">✗ ${unpaidMonths} Ödenmedi</span>
                </div>
            </div>
        </div>
    `;

    const editBtn = document.getElementById('apartment-detail-edit-btn');
    if (editBtn) {
        (editBtn as HTMLButtonElement).onclick = () => {
            deps.closeModal('apartment-detail-modal');
            editApartmentPresenter(normalized.id || normalized.number, deps);
        };
    }

    deps.openModal('apartment-detail-modal');
}
